import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '@/lib/api';
import { Loader2, ChevronDown, ChevronRight, Search, Package, Layers, FileText, X, Lock, LayoutList, GitBranch, Trash2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import PWMDecompositionTree from './PWMDecompositionTree';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444', label: 'Unallocated' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b', label: 'Partial Hard' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e', label: 'Fully Allocated' },
};

const STAGE_COLORS = {
  NOT_STARTED: '#94a3b8', FIT_UP: '#3b82f6', WELDING: '#f59e0b',
  NDT: '#143A6B', PAINTING: '#ec4899', DISPATCHED: '#22c55e', DISPATCH: '#22c55e',
};

function fmtNum(num) {
  if (!num && num !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
}

function fmtPct(v) {
  if (v == null) return '0%';
  return `${Number(v).toFixed(0)}%`;
}

function fmtDate(d) {
  if (!d) return '-';
  return String(d).slice(0, 10);
}

// Spool-level detail panel for PARTIAL_HARD / FULLY_ALLOCATED lines
function SpoolDetailPanel({ projectId, lineId, onClose }) {
  const [data, setData] = useState(null);
  const [gateStatus, setGateStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('spools');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [res, gateRes] = await Promise.all([
          api.get(`/v1/projects/${projectId}/pwm/line-detail`, { params: { lineId } }),
          api.get(`/v1/projects/${projectId}/pwm/spools-gate-status`),
        ]);
        setData(res.data);
        setGateStatus(gateRes.data || {});
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [projectId, lineId]);

  if (loading) return (
    <tr><td colSpan={17} className="p-4">
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
    </td></tr>
  );
  if (!data) return null;

  const { line, spools, spoolProgress, isometrics } = data;
  const tabs = [
    { id: 'spools', label: `Spool BoQ (${spools?.length || 0})`, icon: Package },
    { id: 'progress', label: `Spool Progress (${spoolProgress?.length || 0})`, icon: Layers },
    { id: 'isos', label: `Isometrics (${isometrics?.length || 0})`, icon: FileText },
    { id: 'boq', label: `Line BoQ (${line?.components?.length || 0})`, icon: FileText },
  ];

  return (
    <tr data-testid={`detail-panel-${lineId}`}>
      <td colSpan={17} style={{ padding: 0 }}>
        <div className="mx-4 my-2 rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: '#143A6B40' }}>
          {/* Sub-tab header */}
          <div className="flex items-center gap-1 px-3 py-2 border-b" style={{ background: '#143A6B08', borderColor: '#143A6B20' }}>
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`detail-tab-${t.id}-${lineId}`}
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors"
                  style={{ background: tab === t.id ? '#143A6B' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)' }}>
                  <Icon className="w-3 h-3" /> {t.label}
                </button>
              );
            })}
            <div className="flex-1" />
            <button onClick={onClose} className="p-1 rounded hover:bg-black/10" data-testid={`close-detail-${lineId}`}>
              <X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>

          {/* Spool BoQ Tab — Hard Allocation at Spool Level */}
          {tab === 'spools' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Spool No.</th>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Drawing</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Inch-Dia</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Type</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#10b981', background: 'var(--card-bg-alt)' }}>Alloc %</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Mat Reqs</th>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>SAP Codes</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(spools || []).map(sp => {
                    const pct = sp.overallAllocatedPercent || 0;
                    const mrs = sp.materialRequirements || [];
                    return (
                      <tr key={sp.spoolNumber} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`spool-boq-${sp.spoolNumber}`}>
                        <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{sp.spoolNumber}</td>
                        <td className="p-1.5 font-mono text-[9px]" style={{ color: 'var(--text-muted)' }}>{sp.drawingId}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(sp.spoolInchDia)}</td>
                        <td className="p-1.5 text-center text-[9px]" style={{ color: 'var(--text-muted)' }}>{sp.spoolType?.replace(/_/g, ' ').slice(0, 10)}</td>
                        <td className="p-1.5 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <div className="w-10 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                              <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? '#22c55e' : pct > 0 ? '#eab308' : '#94a3b8' }} />
                            </div>
                            <span className="font-mono font-bold" style={{ color: pct >= 100 ? '#22c55e' : pct > 0 ? '#eab308' : 'var(--text-muted)' }}>{fmtPct(pct)}</span>
                          </div>
                        </td>
                        <td className="p-1.5 text-center" style={{ color: 'var(--text-primary)' }}>{mrs.length}</td>
                        <td className="p-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{mrs.map(m => m.sapCode).join(', ')}</td>
                        <td className="p-1.5 text-center">
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{
                            background: pct >= 100 ? '#22c55e20' : pct > 0 ? '#eab30820' : '#94a3b820',
                            color: pct >= 100 ? '#22c55e' : pct > 0 ? '#eab308' : '#94a3b8'
                          }}>{pct >= 100 ? 'Full' : pct > 0 ? 'Partial' : 'None'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Spool Progress Tab — Fabrication Stage Gates */}
          {tab === 'progress' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Spool No.</th>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Drawing</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>ID</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>IM</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#3b82f6', background: 'var(--card-bg-alt)', borderLeft: '2px solid var(--border-color)' }}>Fit-Up</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#f59e0b', background: 'var(--card-bg-alt)' }}>Welding</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#143A6B', background: 'var(--card-bg-alt)' }}>NDT</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#ec4899', background: 'var(--card-bg-alt)' }}>Paint</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#22c55e', background: 'var(--card-bg-alt)' }}>Dispatch</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)', borderLeft: '2px solid var(--border-color)' }}>Stage</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Fab %</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#143A6B', background: 'var(--card-bg-alt)' }}>Erec</th>
                  </tr>
                </thead>
                <tbody>
                  {(spoolProgress || []).map(sp => {
                    const sg = sp.stageGates || {};
                    const gs = gateStatus[sp.spoolNumber];
                    const allocPct = gs?.allocPct ?? 0;
                    const effectiveStage = allocPct >= 100 ? (sp.currentStage || 'NOT_STARTED') : 'NOT_STARTED';
                    const effectiveFabPct = allocPct >= 100 ? (sp.fabricationProgressPct || 0) : 0;
                    const stc_val = STAGE_COLORS[effectiveStage] || '#94a3b8';
                    const gateDate = (gate) => {
                      if (allocPct < 100) {
                        const reason = gs?.stages?.[gate]?.lockReason || `Hard Allocation ${allocPct.toFixed(0)}% (need 100%)`;
                        return <span title={reason} style={{ color: '#ef4444', cursor: 'help' }}><Lock className="w-3 h-3 inline" /></span>;
                      }
                      const d = sg[gate]?.date;
                      if (d) return <span style={{ color: '#22c55e' }}>{fmtDate(d).slice(5)}</span>;
                      const isLocked = gs?.stages?.[gate]?.isLocked;
                      if (isLocked) {
                        return <span title={gs.stages[gate].lockReason} style={{ color: '#ef4444', cursor: 'help' }}><Lock className="w-3 h-3 inline" /></span>;
                      }
                      return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
                    };
                    return (
                      <tr key={sp.spoolNumber} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{sp.spoolNumber}</td>
                        <td className="p-1.5 font-mono text-[9px]" style={{ color: 'var(--text-muted)' }}>{sp.drawingId}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(sp.spoolInchDia)}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(sp.inchMeter)}</td>
                        <td className="p-1.5 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>{gateDate('fitUp')}</td>
                        <td className="p-1.5 text-center">{gateDate('welding')}</td>
                        <td className="p-1.5 text-center">{gateDate('ndt')}</td>
                        <td className="p-1.5 text-center">{gateDate('painting')}</td>
                        <td className="p-1.5 text-center">{gateDate('dispatch')}</td>
                        <td className="p-1.5 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: stc_val + '20', color: stc_val }}>
                            {effectiveStage.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-1.5 text-center font-mono font-bold" style={{ color: effectiveFabPct >= 80 ? '#22c55e' : effectiveFabPct > 0 ? '#143A6B' : 'var(--text-muted)' }}>
                          {fmtPct(effectiveFabPct)}
                        </td>
                        <td className="p-1.5 text-center text-[9px]" style={{ color: sp.erection?.erectionStatus === 'ERECTED' ? '#22c55e' : 'var(--text-muted)' }}>
                          {sp.erection?.erectionStatus === 'ERECTED' ? fmtDate(sp.erection.erectionDate).slice(5) : sp.erection?.erectionStatus?.replace(/_/g, ' ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Isometrics Tab */}
          {tab === 'isos' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Drawing ID</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>AG/UG</th>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Area</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Inch-Dia</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Inch-Meter</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Spools</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: '#22c55e', background: 'var(--card-bg-alt)' }}>Erected</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Erec %</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(isometrics || []).map(iso => (
                    <tr key={iso.drawingId} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{iso.drawingId}</td>
                      <td className="p-1.5 text-center" style={{ color: iso.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{iso.agUg}</td>
                      <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{iso.area}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(iso.inchDia)}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(iso.inchMeter)}</td>
                      <td className="p-1.5 text-center" style={{ color: 'var(--text-primary)' }}>{iso.totalSpools}</td>
                      <td className="p-1.5 text-center font-bold" style={{ color: '#22c55e' }}>{iso.erectedSpools || 0}</td>
                      <td className="p-1.5 text-center font-mono" style={{ color: '#143A6B' }}>{(iso.erectionProgressPct || 0).toFixed(0)}%</td>
                      <td className="p-1.5 text-center text-[9px]" style={{ color: iso.drawingStatus === 'B' ? '#22c55e' : '#f59e0b' }}>{iso.drawingStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Line BoQ / Components Tab */}
          {tab === 'boq' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>SAP Code</th>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Description</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Inch-Dia</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Qty</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>UoM</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>ID Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {(line?.components || []).map((comp, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{comp.sapCode}</td>
                      <td className="p-1.5" style={{ color: 'var(--text-primary)' }}>{comp.componentDescription}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(comp.inchDia)}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{comp.quantity}</td>
                      <td className="p-1.5 text-center" style={{ color: 'var(--text-muted)' }}>{comp.uom}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: '#143A6B' }}>{fmtNum(comp.inchDiaContribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export default function PWMWorkfrontStatus({ projectId, filters }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateDist, setStateDist] = useState({});
  const [refCounts, setRefCounts] = useState({});
  const [expandedLine, setExpandedLine] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [viewMode, setViewMode] = useState('table');
  // Multi-select for bulk-delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (lineId) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(lineId)) n.delete(lineId); else n.add(lineId);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '2000' });
      if (filters.agUg) params.set('agUg', filters.agUg);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.area) params.set('area', filters.area);
      if (filters.status) params.set('status', filters.status);
      if (search) params.set('search', search);
      const [linesRes, distRes, refsRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pwm/lines?${params}`),
        api.get(`/v1/projects/${projectId}/pwm/allocation/state-distribution`),
        api.get(`/v1/projects/${projectId}/pwm/lines-reference-counts`),
      ]);
      setLines(linesRes.data?.lines || []);
      setStateDist(distRes.data || {});
      setRefCounts(refsRes.data || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, filters, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleLine = (lineId) => setExpandedLine(prev => prev === lineId ? null : lineId);

  /**
   * Cascade-delete a line + every dependent (spools, progress, isometrics). The line
   * row vanishes from the UI immediately; backend wipe is best-effort and is followed
   * by a full refetch so any cascading roll-ups stay in sync.
   */
  const handleDeleteLine = useCallback(async (lineId, e) => {
    e?.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete line "${lineId}" and ALL its spools, progress rows and isometrics?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/v1/projects/${projectId}/pwm/lines`, { params: { lineId } });
      setLines(prev => prev.filter(l => l.lineId !== lineId));
      if (expandedLine === lineId) setExpandedLine(null);
      // Force a re-fetch so distributions/refCounts stay consistent
      fetchData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Delete failed: ' + (err?.response?.data?.detail || err?.message || 'Unknown error'));
    }
  }, [projectId, expandedLine, fetchData]);

  /** Bulk cascade-delete every currently selected line + its dependents. */
  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} line${ids.length === 1 ? '' : 's'} and ALL their spools, progress rows and isometrics?\n\nThis cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/pwm/lines/bulk-delete`, { lineIds: ids });
      setLines(prev => prev.filter(l => !selectedIds.has(l.lineId)));
      clearSelection();
      setExpandedLine(null);
      // eslint-disable-next-line no-alert
      window.alert(`Deleted ${r.data?.lineCount || ids.length} lines (${r.data?.totalDeleted || 0} total docs cascaded).`);
      fetchData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Bulk delete failed: ' + (err?.response?.data?.detail || err?.message || 'Unknown error'));
    } finally {
      setBulkDeleting(false);
    }
  }, [projectId, selectedIds, fetchData]);

  const isHardAllocated = (status) => status === 'PARTIAL_HARD' || status === 'FULLY_ALLOCATED';

  // Clustered bar chart data
  const allStates = ['UNALLOCATED', 'PARTIAL_HARD', 'FULLY_ALLOCATED'];
  const totalLines = Object.values(stateDist).reduce((s, v) => s + (v?.count || 0), 0);
  const chartData = allStates.map(st => ({
    name: STATUS_COLORS[st]?.label || st,
    count: stateDist[st]?.count || 0,
    fill: STATUS_COLORS[st]?.text || '#94a3b8',
    key: st,
  }));

  // Flat sorted lines
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedLines = [...lines].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const getVal = (row) => {
      switch (sortField) {
        case 'lineId': return row.lineId || '';
        case 'agUg': return row.agUg || '';
        case 'area': return row.area || '';
        case 'priority': return row.priority ?? 999;
        case 'totalId': return row.totalLineInchDia || 0;
        case 'softPct': return row.softAllocation?.allocationPercent || 0;
        case 'hardPct': return row.hardAllocation?.allocationPercent || 0;
        case 'status': return row.workfrontStatus || '';
        case 'fabPct': return row.fabricationProgress?.overallFabProgressPct || 0;
        case 'erecPct': return row.erectionProgress?.erectionProgressPct || 0;
        default: return '';
      }
    };
    const va = getVal(a), vb = getVal(b);
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  const SortHeader = ({ field, label, style, className }) => (
    <th className={`p-2 font-medium cursor-pointer select-none hover:opacity-80 ${className || ''}`}
      style={style} onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortField === field && <span className="text-[8px] ml-0.5">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  return (
    <div className="space-y-3" data-testid="pwm-workfront-status">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-1.5" data-testid="pwm-view-toggle">
        <button onClick={() => setViewMode('table')} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors"
          style={{ background: viewMode === 'table' ? '#143A6B' : 'var(--card-bg)', color: viewMode === 'table' ? '#fff' : 'var(--text-muted)', border: viewMode === 'table' ? 'none' : '1px solid var(--border-color)' }}
          data-testid="pwm-view-table">
          <LayoutList className="w-3.5 h-3.5" /> Table View
        </button>
        <button onClick={() => setViewMode('tree')} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors"
          style={{ background: viewMode === 'tree' ? '#143A6B' : 'var(--card-bg)', color: viewMode === 'tree' ? '#fff' : 'var(--text-muted)', border: viewMode === 'tree' ? 'none' : '1px solid var(--border-color)' }}
          data-testid="pwm-view-tree">
          <GitBranch className="w-3.5 h-3.5" /> Decomposition Tree
        </button>
      </div>

      {viewMode === 'tree' ? (
        <PWMDecompositionTree projectId={projectId} filters={filters} />
      ) : (
      <div className="space-y-3">
      {/* Clustered Bar Chart */}
      <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
          Allocation State Distribution ({totalLines} lines)
        </p>
        <div style={{ height: 160 }} data-testid="state-distribution-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -10, bottom: 4 }}
              barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                formatter={(value, name, props) => [
                  `${value} lines (${totalLines > 0 ? ((value / totalLines) * 100).toFixed(1) : 0}%)`,
                  'Count'
                ]}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Search + Bulk-delete toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search line ID..." data-testid="pwm-wf-search"
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ borderColor: 'rgb(239,68,68,0.4)', background: 'rgb(239,68,68,0.08)' }} data-testid="pwm-bulk-toolbar">
            <span className="text-[11px] font-mono" style={{ color: 'rgb(248,113,113)' }}>
              {selectedIds.size} selected
            </span>
            <button onClick={handleBulkDelete} disabled={bulkDeleting}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium transition-colors hover:bg-red-500/20 disabled:opacity-50"
              style={{ color: 'rgb(248,113,113)', border: '1px solid rgb(239,68,68,0.4)' }}
              data-testid="pwm-bulk-delete-btn">
              <Trash2 className="w-3 h-3"/> {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} line${selectedIds.size === 1 ? '' : 's'}`}
            </button>
            <button onClick={clearSelection}
              className="text-[10px] underline" style={{ color: 'var(--text-muted)' }}
              data-testid="pwm-bulk-clear-btn">Clear</button>
          </div>
        )}
      </div>

      {/* Flat Matrix Table — No Hierarchy */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
              <th className="p-2 text-center font-medium w-8">
                <input type="checkbox"
                  checked={sortedLines.length > 0 && sortedLines.every(l => selectedIds.has(l.lineId))}
                  ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !sortedLines.every(l => selectedIds.has(l.lineId)); }}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(sortedLines.map(l => l.lineId)));
                    else clearSelection();
                  }}
                  className="accent-red-500 cursor-pointer"
                  data-testid="pwm-select-all-checkbox"
                  title="Select all visible lines"/>
              </th>
              <th className="p-2 text-left font-medium w-8" />
              <SortHeader field="lineId" label="Line ID" style={{ color: 'var(--text-muted)', minWidth: 150 }} className="text-left" />
              <SortHeader field="agUg" label="AG/UG" style={{ color: 'var(--text-muted)' }} className="text-center w-12" />
              <SortHeader field="area" label="Area" style={{ color: 'var(--text-muted)' }} className="text-left" />
              <SortHeader field="priority" label="P" style={{ color: 'var(--text-muted)' }} className="text-center w-8" />
              <SortHeader field="totalId" label="Total ID" style={{ color: 'var(--text-muted)' }} className="text-right" />
              <SortHeader field="softPct" label="Soft %" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }} className="text-center" />
              <SortHeader field="hardPct" label="Hard %" style={{ color: '#10b981' }} className="text-center" />
              <SortHeader field="status" label="Status" style={{ color: 'var(--text-muted)' }} className="text-center" />
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>Fab Loc</th>
              <SortHeader field="fabPct" label="Fab %" style={{ color: '#f59e0b' }} className="text-center" />
              <SortHeader field="erecPct" label="Erec %" style={{ color: '#143A6B' }} className="text-center" />
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>Line BoQ</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Spool BoQ</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Iso Index</th>
              <th className="p-2 text-center font-medium w-12" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedLines.map((line, idx) => {
              const sc = STATUS_COLORS[line.workfrontStatus] || STATUS_COLORS.UNALLOCATED;
              const softPct = line.softAllocation?.allocationPercent || 0;
              const hardPct = line.hardAllocation?.allocationPercent || 0;
              const fabPct = line.fabricationProgress?.overallFabProgressPct || 0;
              const erecPct = line.erectionProgress?.erectionProgressPct || 0;
              const ref = refCounts[line.lineId] || {};
              const canExpand = isHardAllocated(line.workfrontStatus);
              const isExpanded = expandedLine === line.lineId;
              const stripe = idx % 2 === 0 ? 'var(--card-bg)' : 'var(--card-bg-alt)';

              return (
                <Fragment key={line.lineId}>
                  <tr style={{ borderTop: '1px solid var(--border-color)', background: isExpanded ? '#143A6B08' : (selectedIds.has(line.lineId) ? 'rgb(239,68,68,0.08)' : stripe) }}
                    data-testid={`wf-line-${line.lineId}`}
                    className={canExpand ? 'cursor-pointer' : ''}
                    onClick={canExpand ? () => toggleLine(line.lineId) : undefined}>
                    <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedIds.has(line.lineId)}
                        onChange={() => toggleSelect(line.lineId)}
                        className="accent-red-500 cursor-pointer"
                        data-testid={`pwm-select-${line.lineId}`}/>
                    </td>
                    <td className="p-2">
                      {canExpand ? (
                        isExpanded ? <ChevronDown className="w-3.5 h-3.5" style={{ color: '#143A6B' }} />
                                   : <ChevronRight className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
                      ) : null}
                    </td>
                    <td className="p-2 font-mono text-[10px] font-semibold" style={{ color: 'var(--accent-primary)' }}>{line.lineId}</td>
                    <td className="p-2 text-center text-[10px] font-medium" style={{ color: line.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{line.agUg}</td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{line.area}</td>
                    <td className="p-2 text-center text-[10px] font-bold" style={{ color: 'var(--text-primary)' }}>{line.priority || '-'}</td>
                    <td className="p-2 text-right font-mono text-[10px]" style={{ color: 'var(--text-primary)' }}>{fmtNum(line.totalLineInchDia)}</td>
                    <td className="p-2 text-center font-mono text-[10px]" style={{ color: softPct >= 100 ? '#22c55e' : softPct > 0 ? '#3b82f6' : 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>{fmtPct(softPct)}</td>
                    <td className="p-2 text-center font-mono text-[10px]" style={{ color: hardPct >= 100 ? '#22c55e' : hardPct > 0 ? '#f59e0b' : 'var(--text-muted)' }}>{fmtPct(hardPct)}</td>
                    <td className="p-2 text-center"><span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span></td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>{line.fabricationLocation}</td>
                    <td className="p-2 text-center font-mono text-[10px]" style={{ color: fabPct > 50 ? '#22c55e' : fabPct > 0 ? '#3b82f6' : 'var(--text-muted)' }}>{fmtPct(fabPct)}</td>
                    <td className="p-2 text-center font-mono text-[10px]" style={{ color: erecPct > 50 ? '#22c55e' : erecPct > 0 ? '#143A6B' : 'var(--text-muted)' }}>{fmtPct(erecPct)}</td>
                    <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleLine(line.lineId); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors hover:opacity-80"
                        style={{ background: '#3b82f615', color: '#3b82f6', border: '1px solid #3b82f630' }}
                        data-testid={`ref-boq-${line.lineId}`}>
                        <FileText className="w-2.5 h-2.5" /> {line.totalComponentCount || 0}
                      </button>
                    </td>
                    <td className="p-2 text-center">
                      <button onClick={(e) => { e.stopPropagation(); toggleLine(line.lineId); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors hover:opacity-80"
                        style={{ background: '#f59e0b15', color: '#f59e0b', border: '1px solid #f59e0b30' }}
                        data-testid={`ref-spool-${line.lineId}`}>
                        <Package className="w-2.5 h-2.5" /> {ref.spoolBoqCount || 0}
                      </button>
                    </td>
                    <td className="p-2 text-center">
                      <button onClick={(e) => { e.stopPropagation(); toggleLine(line.lineId); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors hover:opacity-80"
                        style={{ background: '#143A6B15', color: '#143A6B', border: '1px solid #143A6B30' }}
                        data-testid={`ref-iso-${line.lineId}`}>
                        <Layers className="w-2.5 h-2.5" /> {ref.isoCount || 0}
                      </button>
                    </td>
                    <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                      <button onClick={(e) => handleDeleteLine(line.lineId, e)}
                        className="inline-flex items-center justify-center p-1 rounded transition-colors hover:bg-red-500/15 hover:text-red-400"
                        style={{ color: 'var(--text-muted)' }}
                        title={`Delete line ${line.lineId} and all its spools`}
                        data-testid={`delete-line-${line.lineId}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && <SpoolDetailPanel projectId={projectId} lineId={line.lineId} onClose={() => setExpandedLine(null)} />}
                </Fragment>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={17} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No lines found. Seed data from Data Import tab.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
      )}
    </div>
  );
}
